import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { postRequest } from '../../../lib/api';
import { useContext } from 'react';
import { AuthContext } from '../../../authContext/context';
import BreadcrumbsWrapper from '../../../components/breadcrumbs'
import { useDispatch } from 'react-redux';
import { addCustomer } from '../../../redux/slices/customerSlice';
import { checkPermission } from '../../../utils/checkPermissions';
import { Typography } from '@mui/material';

export default function CreateCustomer() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const navigate = useNavigate();
  const dispatch = useDispatch()
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = React.useState(false);
  const [showDuplicatePopup, setShowDuplicatePopup] = React.useState(false);
  const [duplicateInfo, setDuplicateInfo] = React.useState(null);
  const [formData, setFormData] = React.useState(null);

  const userPermissions = authCtx.permissions;
  const isSuperAdmin = authCtx.isSuperAdmin;
  const canCreate = isSuperAdmin || checkPermission(userPermissions, 'customers', 'create');

  if (!canCreate) {
    return (
      <>
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Customers', path: '/customers' },
            { label: 'Create Customer', path: '' },
          ]}
        />
        <br></br>
        <div className='page-header pb-3'>
          <h1 className='mt-0 mb-0'>Create Customer</h1>
        </div>
        <div className='page-top-formview mb-3 text-center'>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to create customers.
          </Typography>
        </div>
      </>
    );
  }

  const createCustomer = async (data, force = false) => {
    const payload = {
      title: data?.title,
      dob: data?.dob,
      email: data?.email,
      forename: data?.forename,
      surname: data?.surname,
      phone_number: data?.phone_number,
      address: {
        address1: data?.address1,
        address2: data?.address2,
        town: data?.town,
        country: data?.country,
        postcode: data?.postcode,
      }
    };

    if (force) {
      payload.forcecreate = true;
    }

    setLoading(true);
    try {
      const response = await postRequest('private/createCustomer', payload);
      if (response?.status === 200) {
        dispatch(addCustomer(response?.data));
        reset();
        setShowDuplicatePopup(false);
        setDuplicateInfo(null);
        setFormData(null);
        navigate('/customers');
      }
    } catch (err) {
      // Check for duplicate customer
      if (err?.status === 409 && err?.isDuplicate) {
        setShowDuplicatePopup(true);
        setDuplicateInfo(err?.duplicateCustomer);
        setFormData(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data) => {
    createCustomer(data, false);
  };

  const handleContinueCreate = () => {
    if (formData) {
      createCustomer(formData, true);
    }
  };

  const handleGoToDuplicate = () => {
    if (duplicateInfo?.customer_id) {
      navigate(`/customer/edit/${duplicateInfo.customer_id}`);
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Customers', path: '/customers' },
          { label: 'Create', path: null }
        ]}
      />
      <br></br>
      <div className="page-header">
        <h1 className="mt-0">Create Customer</h1>
      </div>

      {/* DUPLICATE CUSTOMER POPUP */}
      {showDuplicatePopup && duplicateInfo && (
        <div style={{
          backgroundColor: '#FFF9E6',
          border: '2px solid #FFD700',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ color: '#856404', marginBottom: '10px' }}>
              Potential Duplicate Customer Found! Are you sure you want to proceed?
            </h4>
            <p style={{ color: '#856404', fontSize: '16px', marginBottom: '20px' }}>
              {duplicateInfo.name} - {duplicateInfo.email} - {duplicateInfo.phone_number}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleContinueCreate}
                disabled={loading}
                style={{
                  borderColor: '#DC3545',
                  color: '#DC3545',
                  textTransform: 'none'
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Continue to Create New Customer'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleGoToDuplicate}
                style={{
                  borderColor: '#DC3545',
                  color: '#DC3545',
                  textTransform: 'none'
                }}
              >
                Discard and Go To Duplicate Customer
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="page-top-formview mb-3">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="title">
              <Form.Label>Title</Form.Label>
              <Form.Control type="text" placeholder="Mr/Ms" {...register('title')} />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="forename">
              <Form.Label>Forename</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter forename"
                {...register('forename', { required: 'Forename is required' })}
                isInvalid={!!errors.forename}
              />
              <Form.Control.Feedback type="invalid">
                {errors.forename?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="surname">
              <Form.Label>Surname</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter surname"
                {...register('surname', { required: 'Surname is required' })}
                isInvalid={!!errors.surname}
              />
              <Form.Control.Feedback type="invalid">
                {errors.surname?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="dob">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type="date"
                {...register('dob')}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                })}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="phone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter phone number"
                {...register('phone_number', {
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9]{10,15}$/,
                    message: 'Invalid phone number',
                  },
                })}
                isInvalid={!!errors.phone_number}
              />
              <Form.Control.Feedback type="invalid">
                {errors.phone_number?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Form.Label>Address</Form.Label>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="line1">
              <Form.Control
                type="text"
                placeholder="Line 1"
                {...register('address1')}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="line2">
              <Form.Control
                type="text"
                placeholder="Line 2"
                {...register('address2')}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="town">
              <Form.Control
                type="text"
                placeholder="Town"
                {...register('town')}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="county">
              <Form.Control
                type="text"
                placeholder="County"
                {...register('country')}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="postcode">
              <Form.Control
                type="text"
                placeholder="Postcode"
                {...register('postcode')}
              />
            </Form.Group>
          </Row>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="inherit" type="button" onClick={() => navigate('/customers')}>
              Cancel
            </Button>
            <Button variant="contained" className='red-bg red-button text-capitalize' type="submit" disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
            </Button>
          </Stack>
        </Form>
      </div>
    </>
  );
}
